import { observer } from "mobx-react-lite";
import { FC, ReactNode } from "react";
import styled from "@emotion/styled";
import { PlatformIconKind } from "@/domains/api/types";
import { SpaceAccountAppCalloutModelData } from "@/store/app-callout/types";
import { AppCalloutBannerIcon } from "@/components/banners/app-callout-banner/app-callout-banner-icon";
import { AppCalloutBannerPrimaryLabel } from "@/components/banners/app-callout-banner/app-callout-banner-primary-label";
import { AppCalloutBannerSecondaryLabel } from "@/components/banners/app-callout-banner/app-callout-banner-secondary-label";
import { AppCalloutBannerActionButton } from "@/components/banners/app-callout-banner/app-callout-banner-action-button";
import { AppCalloutBannerSlimPrimaryLabel } from "@/components/banners/app-callout-banner/app-callout-banner-slim-primary-label";
import { AppCalloutBannerFormattedPrimaryLabel } from "@/components/banners/app-callout-banner/app-callout-banner-formatted-primary-label";
import type { AppCalloutBannerEventParams } from "@/domains/metrics/tracked-event";
import { MDSIconButton, MDSIconKind, MDSText, MDSTextVariant } from "@/mds";

interface AppCalloutBannerProps {
  iconKind: PlatformIconKind | null;
  iconPosition: SpaceAccountAppCalloutModelData["icon_position"];
  isDismissible: boolean;
  isDismissLoading?: boolean;
  primaryLabel: string;
  primaryLabelEmphasizedPrefix?: string | null;
  secondaryLabel: string;
  inlineActionLabel: string | null;
  inlineActionUrl: string | null;
  primaryActionLabel: string | null;
  primaryActionUrl: string | null;
  primaryActionIconKind: PlatformIconKind | null;
  primaryActionButtonVariant: string | null;
  secondaryActionLabel: string | null;
  secondaryActionUrl: string | null;
  secondaryActionIconKind: PlatformIconKind | null;
  tertiaryActionLabel: string | null;
  tertiaryActionUrl: string | null;
  tertiaryActionIconKind: PlatformIconKind | null;
  trackingContext: AppCalloutBannerEventParams;
  handleDismiss: () => Promise<void>;
  handlePrimaryActionClick?: () => void;
  handlePrimaryActionComplete?: () => void;
}

export interface AppCalloutBannerActionRendererProps {
  label: string | null;
  url: string | null;
  iconKind: PlatformIconKind | null;
  buttonVariant?: string | null;
  actionSlot: "primary" | "secondary" | "tertiary";
  trackingContext: AppCalloutBannerEventParams;
  onActionClick?: () => void;
  onActionComplete?: () => void;
}

interface AppCalloutBannerContentViewProps extends AppCalloutBannerProps {
  renderActionButton: (props: AppCalloutBannerActionRendererProps) => ReactNode;
}

const renderAppCalloutBannerActionButton = (props: AppCalloutBannerActionRendererProps) => (
  <AppCalloutBannerActionButton {...props} />
);

export const AppCalloutBannerContent: FC<AppCalloutBannerProps> = observer(props => (
  <AppCalloutBannerContentView {...props} renderActionButton={renderAppCalloutBannerActionButton} />
));

export const AppCalloutBannerContentView: FC<AppCalloutBannerContentViewProps> = observer(
  ({
    iconKind,
    iconPosition,
    isDismissible,
    isDismissLoading = false,
    primaryLabel,
    primaryLabelEmphasizedPrefix,
    secondaryLabel,
    inlineActionLabel,
    inlineActionUrl,
    primaryActionLabel,
    primaryActionUrl,
    primaryActionIconKind,
    primaryActionButtonVariant,
    secondaryActionLabel,
    secondaryActionUrl,
    secondaryActionIconKind,
    tertiaryActionLabel,
    tertiaryActionUrl,
    tertiaryActionIconKind,
    trackingContext,
    handleDismiss,
    handlePrimaryActionClick,
    handlePrimaryActionComplete,
    renderActionButton,
  }) => {
    const hasInlineStartIcon = iconKind !== null && iconPosition === "INLINE_START";

    if (hasInlineStartIcon && !secondaryLabel && !inlineActionLabel && !inlineActionUrl) {
      return (
        <InlineStartIconLayoutContent
          iconKind={iconKind}
          isDismissible={isDismissible}
          isDismissLoading={isDismissLoading}
          primaryLabel={primaryLabel}
          primaryLabelEmphasizedPrefix={primaryLabelEmphasizedPrefix}
          primaryActionLabel={primaryActionLabel}
          primaryActionUrl={primaryActionUrl}
          primaryActionIconKind={primaryActionIconKind}
          primaryActionButtonVariant={primaryActionButtonVariant}
          secondaryActionLabel={secondaryActionLabel}
          secondaryActionUrl={secondaryActionUrl}
          secondaryActionIconKind={secondaryActionIconKind}
          tertiaryActionLabel={tertiaryActionLabel}
          tertiaryActionUrl={tertiaryActionUrl}
          tertiaryActionIconKind={tertiaryActionIconKind}
          trackingContext={trackingContext}
          handleDismiss={handleDismiss}
          handlePrimaryActionClick={handlePrimaryActionClick}
          handlePrimaryActionComplete={handlePrimaryActionComplete}
          renderActionButton={renderActionButton}
        />
      );
    }

    if (!iconKind && !secondaryLabel && !inlineActionLabel && !inlineActionUrl) {
      return (
        <SlimLayoutContent
          isDismissible={isDismissible}
          isDismissLoading={isDismissLoading}
          primaryLabel={primaryLabel}
          primaryLabelEmphasizedPrefix={primaryLabelEmphasizedPrefix}
          primaryActionLabel={primaryActionLabel}
          primaryActionUrl={primaryActionUrl}
          primaryActionIconKind={primaryActionIconKind}
          primaryActionButtonVariant={primaryActionButtonVariant}
          secondaryActionLabel={secondaryActionLabel}
          secondaryActionUrl={secondaryActionUrl}
          secondaryActionIconKind={secondaryActionIconKind}
          tertiaryActionLabel={tertiaryActionLabel}
          tertiaryActionUrl={tertiaryActionUrl}
          tertiaryActionIconKind={tertiaryActionIconKind}
          trackingContext={trackingContext}
          handleDismiss={handleDismiss}
          handlePrimaryActionClick={handlePrimaryActionClick}
          handlePrimaryActionComplete={handlePrimaryActionComplete}
          renderActionButton={renderActionButton}
        />
      );
    }

    return (
      <Wrapper>
        <InnerWrapper>
          <LeftSection>
            <AppCalloutBannerIcon iconKind={iconKind} />
            <ContentSection>
              <AppCalloutBannerPrimaryLabel
                label={primaryLabel}
                emphasizedPrefix={primaryLabelEmphasizedPrefix}
              />
              <AppCalloutBannerSecondaryLabel
                label={secondaryLabel}
                inlineActionLabel={inlineActionLabel}
                inlineActionUrl={inlineActionUrl}
                trackingContext={trackingContext}
              />
              <ActionButtons>
                {renderActionButton({
                  label: primaryActionLabel,
                  url: primaryActionUrl,
                  iconKind: primaryActionIconKind,
                  buttonVariant: primaryActionButtonVariant,
                  actionSlot: "primary",
                  trackingContext,
                  onActionClick: handlePrimaryActionClick,
                  onActionComplete: handlePrimaryActionComplete,
                })}
                {renderActionButton({
                  label: secondaryActionLabel,
                  url: secondaryActionUrl,
                  iconKind: secondaryActionIconKind,
                  actionSlot: "secondary",
                  trackingContext,
                })}
                {renderActionButton({
                  label: tertiaryActionLabel,
                  url: tertiaryActionUrl,
                  iconKind: tertiaryActionIconKind,
                  actionSlot: "tertiary",
                  trackingContext,
                })}
              </ActionButtons>
            </ContentSection>
          </LeftSection>
          <RightSection>
            {isDismissible && (
              <DismissButton
                aria-label={isDismissLoading ? "Dismissing app callout" : "Dismiss app callout"}
                iconKind={MDSIconKind.Exit}
                isLoading={isDismissLoading}
                onClick={handleDismiss}
              />
            )}
          </RightSection>
        </InnerWrapper>
      </Wrapper>
    );
  }
);

const SlimLayoutContent: FC<
  Omit<
    AppCalloutBannerContentViewProps,
    "iconKind" | "iconPosition" | "secondaryLabel" | "inlineActionLabel" | "inlineActionUrl"
  >
> = observer(
  ({
    isDismissible,
    isDismissLoading = false,
    primaryLabel,
    primaryLabelEmphasizedPrefix,
    primaryActionLabel,
    primaryActionUrl,
    primaryActionIconKind,
    primaryActionButtonVariant,
    secondaryActionLabel,
    secondaryActionUrl,
    secondaryActionIconKind,
    tertiaryActionLabel,
    tertiaryActionUrl,
    tertiaryActionIconKind,
    trackingContext,
    handleDismiss,
    handlePrimaryActionClick,
    handlePrimaryActionComplete,
    renderActionButton,
  }) => {
    return (
      <Wrapper>
        <SlimInnerWrapper>
          <LeftSection>
            <SlimContentSection>
              <AppCalloutBannerSlimPrimaryLabel
                label={primaryLabel}
                emphasizedPrefix={primaryLabelEmphasizedPrefix}
              />
            </SlimContentSection>
          </LeftSection>
          <SlimRightSection>
            <SlimActionButtons>
              {renderActionButton({
                label: primaryActionLabel,
                url: primaryActionUrl,
                iconKind: primaryActionIconKind,
                buttonVariant: primaryActionButtonVariant,
                actionSlot: "primary",
                trackingContext,
                onActionClick: handlePrimaryActionClick,
                onActionComplete: handlePrimaryActionComplete,
              })}
              {renderActionButton({
                label: secondaryActionLabel,
                url: secondaryActionUrl,
                iconKind: secondaryActionIconKind,
                actionSlot: "secondary",
                trackingContext,
              })}
              {renderActionButton({
                label: tertiaryActionLabel,
                url: tertiaryActionUrl,
                iconKind: tertiaryActionIconKind,
                actionSlot: "tertiary",
                trackingContext,
              })}
            </SlimActionButtons>
            {isDismissible && (
              <DismissButton
                aria-label={isDismissLoading ? "Dismissing app callout" : "Dismiss app callout"}
                iconKind={MDSIconKind.Exit}
                isLoading={isDismissLoading}
                onClick={handleDismiss}
              />
            )}
          </SlimRightSection>
        </SlimInnerWrapper>
      </Wrapper>
    );
  }
);

const InlineStartIconLayoutContent: FC<
  Omit<
    AppCalloutBannerContentViewProps,
    "iconPosition" | "secondaryLabel" | "inlineActionLabel" | "inlineActionUrl"
  > & {
    iconKind: PlatformIconKind;
  }
> = observer(
  ({
    iconKind,
    isDismissible,
    isDismissLoading = false,
    primaryLabel,
    primaryLabelEmphasizedPrefix,
    primaryActionLabel,
    primaryActionUrl,
    primaryActionIconKind,
    primaryActionButtonVariant,
    secondaryActionLabel,
    secondaryActionUrl,
    secondaryActionIconKind,
    tertiaryActionLabel,
    tertiaryActionUrl,
    tertiaryActionIconKind,
    trackingContext,
    handleDismiss,
    handlePrimaryActionClick,
    handlePrimaryActionComplete,
    renderActionButton,
  }) => {
    return (
      <InlineContainer data-component="app-callout-banner-inline-layout">
        <InlineWrapper>
          <InlineLeftSection>
            <AppCalloutBannerIcon iconKind={iconKind} variant="inline" />
            <InlinePrimaryLabelText variant={MDSTextVariant.ParagraphS}>
              <AppCalloutBannerFormattedPrimaryLabel
                label={primaryLabel}
                emphasizedPrefix={primaryLabelEmphasizedPrefix}
              />
            </InlinePrimaryLabelText>
          </InlineLeftSection>
          <InlineRightSection>
            <InlineActionButtons>
              {renderActionButton({
                label: primaryActionLabel,
                url: primaryActionUrl,
                iconKind: primaryActionIconKind,
                buttonVariant: primaryActionButtonVariant,
                actionSlot: "primary",
                trackingContext,
                onActionClick: handlePrimaryActionClick,
                onActionComplete: handlePrimaryActionComplete,
              })}
              {renderActionButton({
                label: secondaryActionLabel,
                url: secondaryActionUrl,
                iconKind: secondaryActionIconKind,
                actionSlot: "secondary",
                trackingContext,
              })}
              {renderActionButton({
                label: tertiaryActionLabel,
                url: tertiaryActionUrl,
                iconKind: tertiaryActionIconKind,
                actionSlot: "tertiary",
                trackingContext,
              })}
            </InlineActionButtons>
            {isDismissible && (
              <InlineDismissButton
                aria-label={isDismissLoading ? "Dismissing app callout" : "Dismiss app callout"}
                iconKind={MDSIconKind.Exit}
                isLoading={isDismissLoading}
                onClick={handleDismiss}
              />
            )}
          </InlineRightSection>
        </InlineWrapper>
      </InlineContainer>
    );
  }
);

const Wrapper = styled.div(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.colors.background.banner.emphasis,
  display: "flex",
  flexDirection: "column",
  containerName: "appCalloutBanner",
}));

const InnerWrapper = styled.div({
  flexWrap: "nowrap",
  borderRadius: "12px",
  height: "100%",
  width: "100%",
  minWidth: "320px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "flex-start",
  flexGrow: 1,
  containerType: "inline-size",
  containerName: "appCalloutBanner",
  paddingLeft: "12px",
  paddingRight: "12px",
});

const LeftSection = styled.div({
  flexWrap: "nowrap",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  paddingLeft: "16px",
  paddingTop: "16px",
  "@container appCalloutBanner (min-width: 440px)": {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: "0px",
  },
});

const ContentSection = styled.div({
  paddingTop: "0px",
  paddingBottom: "24px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  "@container appCalloutBanner (min-width: 440px)": {
    paddingTop: "24px",
  },
});

const RightSection = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
});

const ActionButtons = styled.div({
  flexWrap: "wrap",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "8px",
});

const DismissButton = styled(MDSIconButton)({
  margin: "8px",
});

const SlimInnerWrapper = styled.div({
  flexWrap: "nowrap",
  borderRadius: "12px",
  height: "100%",
  width: "100%",
  minWidth: "300px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  flexGrow: 1,
  containerType: "inline-size",
  containerName: "appCalloutBanner",
  paddingLeft: "12px",
  paddingRight: "12px",
});

const SlimContentSection = styled.div({
  paddingTop: "0px",
  paddingBottom: "24px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "8px",
  "@container appCalloutBanner (min-width: 440px)": {
    paddingTop: "24px",
  },
});

const SlimRightSection = styled.div({
  height: "100%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "8px",
});

const SlimActionButtons = styled.div({
  flexWrap: "wrap",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "8px",
});

const InlineContainer = styled(Wrapper)({
  containerType: "inline-size",
});

const InlineWrapper = styled.div(({ theme }) => ({
  width: "100%",
  minHeight: "56px",
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: theme.spacing[3],
  paddingTop: theme.spacing[2],
  paddingBottom: theme.spacing[2],
  paddingLeft: theme.spacing[7],
  paddingRight: theme.spacing[5],
  "@container appCalloutBanner (max-width: 900px)": {
    alignItems: "stretch",
    flexDirection: "column",
    gap: theme.spacing[2],
    paddingLeft: theme.spacing[4],
    paddingRight: theme.spacing[4],
  },
}));

const InlineLeftSection = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing[2],
  flex: "1 1 auto",
  minWidth: 0,
  "@container appCalloutBanner (max-width: 900px)": {
    alignItems: "flex-start",
    width: "100%",
  },
}));

const InlinePrimaryLabelText = styled(MDSText)({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "@container appCalloutBanner (max-width: 900px)": {
    overflow: "visible",
    textOverflow: "clip",
    whiteSpace: "normal",
  },
});

const InlineRightSection = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: theme.spacing[1],
  flexShrink: 0,
  "@container appCalloutBanner (max-width: 900px)": {
    alignSelf: "flex-end",
  },
}));

const InlineActionButtons = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: theme.spacing[1],
  flexShrink: 0,
}));

const InlineDismissButton = styled(MDSIconButton)({
  flexShrink: 0,
});
